/**
 * Registers the UNIVERSAL support CLIENT tools (`report_bug`,
 * `escalate_to_human`) with the AI tool bridge. Mounted once inside the
 * AiAssistantProvider core, so the handlers exist in EVERY scope — event chat,
 * company/dashboard chat, and the builder panels — unlike the page-scoped
 * guest-list tools. Renders nothing.
 *
 * Both handlers post to the user's own support session (identity is stamped
 * server-side) and never touch the AI PAT. `escalate_to_human` additionally
 * opens the Crisp live-chat widget so the user lands with a human.
 */
import { useEffect, useRef } from "react";
import { useAiToolBridge } from "@/lib/ai/ai-tool-bridge";
import { useAiAssistant } from "@/providers/AiAssistantProvider";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { queryClient } from "@/lib/api/queryClient";
import { qk } from "@/lib/query/queryKeys";
import type { User } from "@/types/User";
import { reportBug, escalate, type SupportContext } from "@/lib/api/clients/SupportClient";
import {
  setCrispContext,
  openCrispWithSummary,
  isCrispAvailable,
} from "@/lib/integrations/crisp";

export function AiSupportTools() {
  const { scopeEventId, getConversationId } = useAiAssistant();

  // Handlers register ONCE (stable identity for the unregister race-guard);
  // latest context is read through refs at call time. Identity is read from the
  // React Query CACHE (not the useUser hook) so this component never needs a
  // QueryClientProvider at render — it mounts in every AI scope.
  const eventIdRef = useRef(scopeEventId);
  eventIdRef.current = scopeEventId;
  const getConversationIdRef = useRef(getConversationId);
  getConversationIdRef.current = getConversationId;

  useEffect(() => {
    const { register, unregister } = useAiToolBridge.getState();

    const currentUser = (): User | undefined =>
      queryClient.getQueryData<User>(qk.user.me);

    const baseContext = (): SupportContext => {
      const surface = useAiAvailabilityStore.getState().builderSurface;
      return {
        eventId: eventIdRef.current,
        conversationId: getConversationIdRef.current(),
        surface: surface ? `builder:${surface}` : null,
      };
    };

    const reporterName = (): string | undefined => {
      const u = currentUser();
      const name = `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim();
      return name || undefined;
    };

    const reportBugHandler = async (rawInput: unknown) => {
      const input = (rawInput ?? {}) as { message?: string; severity?: string };
      const message = (input.message ?? "").trim();
      if (!message) return { ok: false, errorCode: "messageRequired" };
      // Models sometimes emit out-of-enum severities ("critical"/"urgent") —
      // normalize so a stray value never blocks a valid report.
      const severity = ["low", "normal", "high"].includes(input.severity ?? "")
        ? input.severity
        : undefined;
      try {
        await reportBug({
          message,
          severity,
          context: baseContext(),
        });
        return { ok: true };
      } catch {
        return { ok: false, errorCode: "supportSubmitFailed" };
      }
    };

    const escalateHandler = async (rawInput: unknown) => {
      const input = (rawInput ?? {}) as { reason?: string; summary?: string };
      const reason = (input.reason ?? "").trim();
      const summary = (input.summary ?? "").trim();
      if (!reason) return { ok: false, errorCode: "reasonRequired" };
      const ctx = baseContext();
      // The server-side escalate is the RELIABLE handoff: it creates the Crisp
      // ticket carrying the full transcript + context and confirms delivery
      // (REST 2xx). We always fire it — `isCrispAvailable()` only means the
      // widget id is configured, NOT that the widget actually loaded (ad-block /
      // CSP / network can silently swallow the embed), so relying on the widget
      // alone could strand the user with no record. The live widget is opened on
      // top as a best-effort bonus when available; the slight "two views" (a
      // transcript ticket + a live thread) is acceptable — the ticket is the
      // source of truth, the widget is the live channel.
      let serverOk = false;
      try {
        await escalate({ reason, summary, conversationId: ctx.conversationId, eventId: ctx.eventId, context: ctx });
        serverOk = true;
      } catch {
        serverOk = false;
      }
      let live = false;
      if (isCrispAvailable()) {
        setCrispContext({
          email: currentUser()?.email,
          nickname: reporterName(),
          segments: ["live", "ai-escalation"],
          data: {
            ...(ctx.eventId ? { event_id: ctx.eventId } : {}),
            ...(ctx.conversationId ? { conversation_id: ctx.conversationId } : {}),
            // origin+pathname only — never the query string (token leakage).
            page:
              typeof window !== "undefined"
                ? `${window.location.origin}${window.location.pathname}`
                : "",
          },
        });
        openCrispWithSummary(summary || reason);
        live = true;
      }
      // Only claim success if the reliable server handoff worked OR the live
      // widget opened — never both-failed.
      if (serverOk || live) return { ok: true, connectedToHuman: live };
      return { ok: false, errorCode: "escalationFailed" };
    };

    register("report_bug", reportBugHandler);
    register("escalate_to_human", escalateHandler);
    return () => {
      unregister("report_bug", reportBugHandler);
      unregister("escalate_to_human", escalateHandler);
    };
  }, []);

  return null;
}
